/**
 * The frameless window's own title bar.
 *
 * The whole strip is a drag handle apart from the controls, which have to opt
 * back out - otherwise the buttons move the window instead of being clicked.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { bridge, isDesktop } from '@/lib/bridge';

export function TitleBar({ onSearch }: { onSearch?: () => void }) {
  const connection = useSession((s) => s.connection);
  const ping = useSession((s) => s.ping);
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    const api = bridge();
    if (!api?.onWindowState) return;
    return api.onWindowState((s) => setMaximized(!!s.maximized));
  }, []);

  const label =
    connection === 'open' ? 'Connected'
      : connection === 'connecting' ? 'Connecting'
        : connection === 'reconnecting' ? 'Reconnecting'
          : 'Offline';

  return (
    <header data-chrome="nav" className="drag flex h-[var(--titlebar)] flex-none items-center gap-3 border-b border-line-soft bg-surface-0 px-3">
      <div className="flex items-center gap-2.5">
        <img src="./logo.png" alt="" width={22} height={22} className="rounded-md object-contain" />
        <span className="text-[13px] font-extrabold tracking-[.18em] text-dim">PUGO</span>
      </div>

      <div className="flex-1" />

      {onSearch ? (
        <button
          type="button"
          onClick={onSearch}
          title="Search and jump anywhere (Ctrl+K)"
          className="no-drag flex h-[26px] items-center gap-2 rounded-full border border-line-soft bg-surface-1 pr-1.5 pl-2.5 text-[12px] text-mute transition-colors hover:border-line hover:bg-surface-2 hover:text-dim"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4-4" />
          </svg>
          <span>Search</span>
          <kbd className="rounded border border-line-soft bg-surface-3 px-1.5 py-px font-mono text-[10px] text-ghost">Ctrl K</kbd>
        </button>
      ) : null}


      <div className="no-drag flex items-center gap-2 text-[12px] text-mute">
        <i
          className={cn(
            'h-[7px] w-[7px] rounded-full',
            connection === 'open' ? 'bg-good' : connection === 'closed' ? 'bg-bad' : 'bg-warn'
          )}
        />
        <span>{label}</span>
        {ping !== null && connection === 'open' ? (
          <span className="text-ghost tabular-nums">{ping} ms</span>
        ) : null}
      </div>

      {isDesktop() ? (
        <div className="no-drag ml-2 flex items-center">
          <WinButton label="Minimize" onClick={() => bridge()?.window('minimize')}>
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 6h8" stroke="currentColor" strokeWidth="1.4" /></svg>
          </WinButton>
          <WinButton label={maximized ? 'Restore' : 'Maximize'} onClick={() => bridge()?.window('maximize')}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3">
              <rect x="2.4" y="2.4" width="7.2" height="7.2" rx="1.3" />
            </svg>
          </WinButton>
          <WinButton label="Close" danger onClick={() => bridge()?.window('close')}>
            <svg width="12" height="12" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.4">
              <path d="M3 3l6 6M9 3l-6 6" />
            </svg>
          </WinButton>
        </div>
      ) : null}
    </header>
  );
}

function WinButton({
  children, label, onClick, danger
}: { children: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'grid h-8 w-11 place-items-center rounded-md text-mute transition-colors',
        danger ? 'hover:bg-bad hover:text-white' : 'hover:bg-surface-3 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}
